import { useState } from 'react'
import {
  LayoutDashboard,
  Ticket,
  BarChart3,
  Activity,
  Settings,
  ArrowLeft,
  RefreshCw,
  Users,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useUserTier } from '@/lib/api/hooks'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

// Import tab content components
import { PromotionalCodesTab } from '@/components/admin/PromotionalCodesTab'
import { OutreachTab } from '@/components/admin/OutreachTab'
import { UsageStatsTab } from '@/components/admin/UsageStatsTab'
import { SystemHealthTab } from '@/components/admin/SystemHealthTab'

export default function AdminDashboard() {
  const { data: user, isLoading } = useUserTier()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('analytics')

  // Redirect if not superuser
  if (!isLoading && !user?.is_superuser) {
    navigate('/dashboard')
    return null
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto py-8 px-4 max-w-7xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <LayoutDashboard className="h-8 w-8 text-primary" />
              Admin Dashboard
            </h1>
            <p className="text-muted-foreground">
              Manage promotions, campaigns, and platform settings
            </p>
          </div>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-5 lg:w-auto lg:inline-grid">
            <TabsTrigger value="analytics" className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              <span className="hidden sm:inline">Usage Stats</span>
            </TabsTrigger>
            <TabsTrigger value="health" className="flex items-center gap-2">
              <Activity className="h-4 w-4" />
              <span className="hidden sm:inline">System Health</span>
            </TabsTrigger>
            <TabsTrigger value="promotions" className="flex items-center gap-2">
              <Ticket className="h-4 w-4" />
              <span className="hidden sm:inline">Promotions</span>
            </TabsTrigger>
            <TabsTrigger value="outreach" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              <span className="hidden sm:inline">Outreach</span>
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              <span className="hidden sm:inline">Settings</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="analytics">
            <UsageStatsTab />
          </TabsContent>

          <TabsContent value="health">
            <SystemHealthTab />
          </TabsContent>

          <TabsContent value="promotions">
            <PromotionalCodesTab />
          </TabsContent>

          <TabsContent value="outreach">
            <OutreachTab />
          </TabsContent>

          <TabsContent value="settings">
            <div className="text-center py-16 text-muted-foreground">
              <Settings className="h-16 w-16 mx-auto mb-4 opacity-50" />
              <p>Settings panel coming soon</p>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}
